/**
 * Positionsübergreifende Verrechnung von steuerpflichtigen Kursgewinnen/-verlusten
 * vor Anwendung von Sparer-Pauschbetrag und Abgeltungsteuer (Modelljahressicht).
 */

import type { SteuerParameter } from '@/types';
import { calculateEffectiveCapitalGainsTax } from '@/utils/constants';

export interface GewinnRow {
  positionKey: string;
  gewinn: number;
}

export interface NetKapitalsteuerErgebnis {
  /** Summe steuerpflichtiger Gewinne/Verluste aller Zeilen (Verrechnung) */
  summeGewinneRohes: number;
  nettoNachVerrechnung: number;
  verwendeterPauschbetrag: number;
  steuerpflichtigerGewinnNachPausch: number;
  kapitalertragsteuer: number;
  solidaritaetszuschlag: number;
  kirchensteuer: number;
  gesamtsteuer: number;
  gesamtsteuerProPosition: Map<string, number>;
  kapitalertragsteuerProPosition: Map<string, number>;
  solidaritaetszuschlagProPosition: Map<string, number>;
  kirchensteuerProPosition: Map<string, number>;
  verwendeterPauschbetragProPosition: Map<string, number>;
  /** Anteil am Gesamt-Gewinn nach Verrechnung/Pausch (proportional zu max(0, FIFO-Gewinn)) */
  steuerpflichtigerGewinnNachPauschProPosition: Map<string, number>;
}

function allocateByPositiveGewinn(rows: GewinnRow[], totalAmount: number): Map<string, number> {
  const map = new Map<string, number>();
  const sumW = rows.reduce((s, r) => s + Math.max(0, r.gewinn), 0);
  if (sumW <= 0 || totalAmount <= 0) {
    rows.forEach((r) => map.set(r.positionKey, 0));
    return map;
  }
  let allocated = 0;
  const positiveRows = rows.filter((r) => r.gewinn > 0);
  positiveRows.forEach((r, idx) => {
    if (idx === positiveRows.length - 1) {
      map.set(r.positionKey, Math.round((totalAmount - allocated) * 10000) / 10000);
    } else {
      const part = Math.round(totalAmount * (r.gewinn / sumW) * 10000) / 10000;
      map.set(r.positionKey, part);
      allocated += part;
    }
  });
  rows.filter((r) => r.gewinn <= 0).forEach((r) => map.set(r.positionKey, 0));
  return map;
}

/**
 * Verteilt Gesamtsteuer auf Positionen (Gewicht max(0, Gewinn)), dann KapSt und SolZ
 * proportional zu g_i mit Rundungsrest auf der letzten positiven Zeile; KiSt = g − k − s.
 * So gilt je Position k + s + ki = g sowie Σk = KapSt, Σs = SolZ, Σki = KiSt.
 */
function allocateSteuerkomponentenKonsistent(
  rows: GewinnRow[],
  tax: {
    kapitalertragsteuer: number;
    solidaritaetszuschlag: number;
    kirchensteuer: number;
    gesamtsteuer: number;
  }
): {
  kapitalertragsteuerProPosition: Map<string, number>;
  solidaritaetszuschlagProPosition: Map<string, number>;
  kirchensteuerProPosition: Map<string, number>;
  gesamtsteuerProPosition: Map<string, number>;
} {
  const gesamtsteuerProPosition = allocateByPositiveGewinn(rows, tax.gesamtsteuer);
  const kapitalertragsteuerProPosition = new Map<string, number>();
  const solidaritaetszuschlagProPosition = new Map<string, number>();
  const kirchensteuerProPosition = new Map<string, number>();

  rows.forEach((r) => {
    kapitalertragsteuerProPosition.set(r.positionKey, 0);
    solidaritaetszuschlagProPosition.set(r.positionKey, 0);
    kirchensteuerProPosition.set(r.positionKey, 0);
  });

  const G = tax.gesamtsteuer;
  if (G <= 0) {
    return {
      gesamtsteuerProPosition,
      kapitalertragsteuerProPosition,
      solidaritaetszuschlagProPosition,
      kirchensteuerProPosition
    };
  }

  const Kap = tax.kapitalertragsteuer;
  const Soli = tax.solidaritaetszuschlag;
  const ratioK = Kap / G;
  const ratioS = Soli / G;

  const positiveRows = rows.filter((r) => r.gewinn > 0);
  let accK = 0;
  let accS = 0;

  positiveRows.forEach((r, idx) => {
    const g = gesamtsteuerProPosition.get(r.positionKey) ?? 0;
    if (idx === positiveRows.length - 1) {
      const k = Math.round((Kap - accK) * 10000) / 10000;
      const s = Math.round((Soli - accS) * 10000) / 10000;
      const ki = Math.round((g - k - s) * 10000) / 10000;
      kapitalertragsteuerProPosition.set(r.positionKey, k);
      solidaritaetszuschlagProPosition.set(r.positionKey, s);
      kirchensteuerProPosition.set(r.positionKey, ki);
    } else {
      const k = Math.round(g * ratioK * 10000) / 10000;
      const s = Math.round(g * ratioS * 10000) / 10000;
      const ki = Math.round((g - k - s) * 10000) / 10000;
      kapitalertragsteuerProPosition.set(r.positionKey, k);
      solidaritaetszuschlagProPosition.set(r.positionKey, s);
      kirchensteuerProPosition.set(r.positionKey, ki);
      accK += k;
      accS += s;
    }
  });

  return {
    gesamtsteuerProPosition,
    kapitalertragsteuerProPosition,
    solidaritaetszuschlagProPosition,
    kirchensteuerProPosition
  };
}

/**
 * Eine gemeinsame Bemessungsgrundlage: Summe der steuerpflichtigen Gewinne/Verluste,
 * dann Sparer-Pauschbetrag auf den positiven Saldo, dann eine KapSt-Berechnung.
 */
export function berechneNettoKapitalsteuerNachVerrechnung(
  rows: GewinnRow[],
  steuerParameter: SteuerParameter | null,
  sparerPauschbetragEnabled: boolean,
  sparerPauschbetragBetrag: number
): NetKapitalsteuerErgebnis {
  const summeGewinneRohes = rows.reduce((s, r) => s + r.gewinn, 0);
  const nettoNachVerrechnung = summeGewinneRohes;
  const verwendeterPauschbetrag = sparerPauschbetragEnabled
    ? Math.min(sparerPauschbetragBetrag, Math.max(0, nettoNachVerrechnung))
    : 0;
  const steuerpflichtigerGewinnNachPausch = Math.max(0, nettoNachVerrechnung - verwendeterPauschbetrag);

  const tax = calculateEffectiveCapitalGainsTax(
    steuerpflichtigerGewinnNachPausch,
    steuerParameter?.kirchensteuer
  );

  const steuerProPosition = allocateSteuerkomponentenKonsistent(rows, tax);

  return {
    summeGewinneRohes,
    nettoNachVerrechnung,
    verwendeterPauschbetrag,
    steuerpflichtigerGewinnNachPausch,
    kapitalertragsteuer: tax.kapitalertragsteuer,
    solidaritaetszuschlag: tax.solidaritaetszuschlag,
    kirchensteuer: tax.kirchensteuer,
    gesamtsteuer: tax.gesamtsteuer,
    gesamtsteuerProPosition: steuerProPosition.gesamtsteuerProPosition,
    kapitalertragsteuerProPosition: steuerProPosition.kapitalertragsteuerProPosition,
    solidaritaetszuschlagProPosition: steuerProPosition.solidaritaetszuschlagProPosition,
    kirchensteuerProPosition: steuerProPosition.kirchensteuerProPosition,
    verwendeterPauschbetragProPosition: allocateByPositiveGewinn(rows, verwendeterPauschbetrag),
    steuerpflichtigerGewinnNachPauschProPosition: allocateByPositiveGewinn(
      rows,
      steuerpflichtigerGewinnNachPausch
    ),
  };
}

export function portfolioPositionKey(pos: { identifier: string; portfolio?: string }): string {
  return `${pos.identifier}|${pos.portfolio || 'Unknown'}`;
}
